import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const OfficeContext = createContext(null);

const ICON = {
  NEW_APPOINTMENT: "📅",
  NEW_MEMO_BROADCAST: "📝",
  CHAT_MESSAGE: "💬",
};

export function OfficeProvider({ children }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [connected, setConnected] = useState(false);
  const [unreadMailCount, setUnreadMailCount] = useState(0);
  const socketRef = useRef(null);
  const retryRef = useRef(null);
  const listenersRef = useRef(new Set());

  const unread = notifications.filter((n) => !n.read).length;

  // Pub/sub so isolated pages (Chat) can react without re-rendering the ERP.
  const subscribe = useCallback((cb) => {
    listenersRef.current.add(cb);
    return () => listenersRef.current.delete(cb);
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const { data } = await api.get("/office/notifications");
      setNotifications(data);
    } catch (_e) {
      /* ignore */
    }
  }, []);

  const refreshMailUnread = useCallback(async () => {
    if (!user?.user_id) {
      setUnreadMailCount(0);
      return;
    }
    try {
      const { data } = await api.get("/office/internal-mail/unread-count");
      setUnreadMailCount(data.count || 0);
    } catch (_e) {
      /* ignore */
    }
  }, [user?.user_id]);

  const markAllRead = useCallback(async () => {
    try {
      await api.post("/office/notifications/read-all");
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (_e) {
      /* ignore */
    }
  }, []);

  const markNotificationRead = useCallback(async (noteId) => {
    if (!noteId) return;
    setNotifications((prev) => prev.filter((note) => note.id !== noteId));
    if (noteId.startsWith("live_")) return;
    try {
      await api.post(`/office/notifications/${noteId}/read`);
    } catch (_e) {
      loadNotifications();
    }
  }, [loadNotifications]);

  useEffect(() => {
    if (!user?.user_id) return;
    loadNotifications();
    refreshMailUnread();

    if (typeof window !== "undefined" && "Notification" in window && window.Notification.permission === "default") {
      const promptKey = `notification-prompted:${user.user_id}`;
      if (!window.sessionStorage.getItem(promptKey)) {
        window.sessionStorage.setItem(promptKey, "true");
        window.Notification.requestPermission().catch(() => {});
      }
    }

    let closedByUs = false;
    let pingTimer = null;
    const mailCountTimer = setInterval(refreshMailUnread, 60000);

    const connect = () => {
      const configuredBase = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const wsUrl = configuredBase.replace(/^http/, "ws").replace(/\/+$/, "") + "/api/office/ws";
      let ws;
      try {
        ws = new WebSocket(wsUrl);
      } catch (_e) {
        return;
      }
      socketRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        pingTimer = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send("ping");
        }, 25000);
      };
      ws.onmessage = (evt) => {
        let data;
        try {
          data = JSON.parse(evt.data);
        } catch (_e) {
          return;
        }
        // fan-out to subscribers (chat, etc.)
        listenersRef.current.forEach((cb) => {
          try {
            cb(data);
          } catch (_e) {
            /* ignore */
          }
        });

        if (data.event === "CHAT_MESSAGE" || data.event === "DOC_LIVE") return; // handled by pages, no toast
        const title = `${ICON[data.event] || "🔔"} ${data.title || "New notification"}`;
        toast(title, { description: data.body || "" });
        if (typeof window !== "undefined" && "Notification" in window && window.Notification.permission === "granted") {
          const browserNote = new window.Notification(data.title || "New notification", {
            body: data.body || "",
            tag: data.ref?.mail_id || `${data.event}-${Date.now()}`,
          });
          browserNote.onclick = () => {
            window.focus();
            if (data.event === "INTERNAL_MAIL") window.location.assign("/office/mail");
            browserNote.close();
          };
        }
        if (data.event === "INTERNAL_MAIL") refreshMailUnread();
        setNotifications((prev) => [
          { id: data.id || `live_${Date.now()}`, event: data.event, title: data.title, body: data.body, ref: data.ref || {}, read: false, created_at: new Date().toISOString() },
          ...prev,
        ]);
      };
      ws.onclose = () => {
        setConnected(false);
        if (pingTimer) clearInterval(pingTimer);
        if (!closedByUs) {
          retryRef.current = setTimeout(connect, 4000);
        }
      };
      // Let onclose own reconnection; don't force-close a CONNECTING socket.
      ws.onerror = () => {};
    };

    connect();
    return () => {
      closedByUs = true;
      if (pingTimer) clearInterval(pingTimer);
      clearInterval(mailCountTimer);
      if (retryRef.current) clearTimeout(retryRef.current);
      const ws = socketRef.current;
      if (ws) {
        ws.onclose = null;
        // Only close an established socket; closing a CONNECTING one spams the console.
        if (ws.readyState === WebSocket.OPEN) {
          try { ws.close(); } catch (_e) { /* ignore */ }
        } else if (ws.readyState === WebSocket.CONNECTING) {
          ws.onopen = () => { try { ws.close(); } catch (_e) { /* ignore */ } };
        }
      }
    };
  }, [user?.user_id, loadNotifications, refreshMailUnread]);

  return (
    <OfficeContext.Provider
      value={{ notifications, unread, connected, subscribe, markNotificationRead, markAllRead, loadNotifications, unreadMailCount, refreshMailUnread }}
    >
      {children}
    </OfficeContext.Provider>
  );
}

export function useOffice() {
  return useContext(OfficeContext) || {};
}
